import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getSessions } from '../../config/sessionsApi';
import { AdminSessionList } from '../../types/sessions';
import SessionList from './SessionList';
import SessionDetail from './SessionDetail';
import Filters from './Filters';

const SessionsPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [sessionsData, setSessionsData] = useState<AdminSessionList | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);

    // Filter states
    const statusFilter = searchParams.getAll('status');
    const cohortFilter = searchParams.getAll('cohort');
    const conditionFilter = searchParams.getAll('condition');
    const qFilter = searchParams.get('q') || '';

    // We update URL and the list re-fetches or filters
    const updateSearchParams = (key: string, value: string | string[]) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete(key);
        if (Array.isArray(value)) {
            value.forEach(v => newParams.append(key, v));
        } else if (value) {
            newParams.set(key, value);
        }
        setSearchParams(newParams);
    };

    useEffect(() => {
        const fetchSessions = async () => {
            setIsLoading(true);
            setError(null);
            try {
                // Collect params for API
                const params: Record<string, string> = {};
                searchParams.forEach((val, key) => {
                    // For multiple values with same key, simple Record might overwrite.
                    // Depending on API, we might need a different structure, but we'll stick to a simple mapping for now
                    if (params[key]) {
                        params[key] += `,${val}`;
                    } else {
                        params[key] = val;
                    }
                });
                const data = await getSessions(params);
                setSessionsData(data);
            } catch (e: any) {
                console.error(e);
                setError(e.message || "An error occurred while fetching sessions.");
            } finally {
                setIsLoading(false);
            }
        };
        fetchSessions();
    }, [searchParams]);

    const showDetail = selectedSessionId !== null;

    const currentIndex = sessionsData?.items.findIndex(s => s.session_id === selectedSessionId) ?? -1;
    const totalCount = sessionsData?.items.length ?? 0;

    const handleNext = () => {
        if (currentIndex !== -1 && currentIndex < totalCount - 1) {
            setSelectedSessionId(sessionsData!.items[currentIndex + 1].session_id);
        }
    };

    const handlePrev = () => {
        if (currentIndex > 0) {
            setSelectedSessionId(sessionsData!.items[currentIndex - 1].session_id);
        }
    };

    const handleClose = () => {
        setSelectedSessionId(null);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '1rem', overflow: 'hidden' }}>
            <Filters
                status={statusFilter}
                cohort={cohortFilter}
                condition={conditionFilter}
                q={qFilter}
                onChange={updateSearchParams}
            />

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden', marginTop: '1rem', gap: '1rem' }}>
                <div style={{ flex: '1', overflow: 'auto' }}>
                    <SessionList
                        data={sessionsData}
                        isLoading={isLoading}
                        error={error}
                        onSelectSession={(id) => setSelectedSessionId(id)}
                        selectedSessionId={selectedSessionId}
                    />
                </div>
            </div>

            {showDetail && (
                <SessionDetail
                    sessionId={selectedSessionId!}
                    onClose={handleClose}
                    onNext={currentIndex < totalCount - 1 ? handleNext : undefined}
                    onPrev={currentIndex > 0 ? handlePrev : undefined}
                    currentIndex={currentIndex !== -1 ? currentIndex + 1 : undefined}
                    totalCount={totalCount > 0 ? totalCount : undefined}
                />
            )}
        </div>
    );
};

export default SessionsPage;